import {BarStack} from '@visx/shape';
import React from 'react';

import {DataFrameRow} from 'data-frames/DataFrameTypes';

import BarChartBar from './BarChartBar';
import {BarChartAccessors, BarChartContentProps, BarGroupRowMaps} from './BarChartTypes';

export function getStackedBarChartYExtent(
  barGroupRowMaps: BarGroupRowMaps,
  yAccessor: BarChartAccessors['yAccessor'],
  yValues: Array<number>
): [number, number] {
  const stackedYValues = barGroupRowMaps.map(({rowMap}) =>
    Object.values(rowMap).reduce((acc, row) => acc + yAccessor(row), 0)
  );

  return [Math.min(...yValues), Math.max(...stackedYValues)];
}

export default function StackedBarChartContent({
  accessors: {rowIdAccessor, seriesAccessor, xAccessor, yAccessor},
  barGroupDicts,
  barGroupRowMaps,
  chartWidth,
  chartHeight,
  handleOnHoverBar,
  highlightedSeries,
  hoveredRow,
  isSeriesEnabled,
  seriesUniqueValues,
  scales: {colorScale, xScale, yScale},
}: BarChartContentProps): JSX.Element {
  return (
    <BarStack
      color={colorScale}
      data={barGroupDicts}
      keys={seriesUniqueValues}
      height={chartHeight}
      width={chartWidth}
      x={xAccessor}
      xScale={xScale}
      // @ts-expect-error BarStack doesn't support scales that return undefined type-wise, but our scale
      // does return undefined when we try to put data into it that doesn't exist (e.g. in cases where
      // the data is sparse, meaning we don't have every bar for every group). We want to return undefined
      // in that case, but that will make BarStack just draw a full height bar. Instead we just handle this
      // case by not rendering a BarStack when we get undefined values in bars. This all means we have
      // to incorrectly use this type.
      yScale={yScale}
    >
      {(barStackLayer) =>
        barStackLayer.map(({bars}, barStackLayerIndex) =>
          bars.map((barProps, barIndex) => {
            const barRow: DataFrameRow | undefined = barGroupRowMaps[barProps.index].rowMap[barProps.key];

            if (barRow == null) {
              return null;
            }

            return (
              barRow && (
                <BarChartBar
                  {...barProps}
                  stepWidth={xScale.step()}
                  dataFrameRow={barRow}
                  isFaded={
                    isSeriesEnabled
                      ? highlightedSeries != null && seriesAccessor(barRow) !== highlightedSeries
                      : hoveredRow != null && rowIdAccessor(barRow) !== rowIdAccessor(hoveredRow)
                  }
                  key={`bar-stack-layer-${barStackLayerIndex}-bar-${barIndex}`}
                  onHover={(hoverInfo) => {
                    handleOnHoverBar(hoverInfo != null ? {...hoverInfo, barGroupInfo: {x: 0}} : hoverInfo);
                  }}
                />
              )
            );
          })
        )
      }
    </BarStack>
  );
}
